import classNames from "classnames";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import useMediaQuery from "../../shared/hooks/useMediaQuery";
import KuikaSvg from "../ui-kit/kuika-svg/kuika-svg";
import type { AuthenticationBannerFeature, AuthenticationBannerIllustration } from "./authentication-banner-features";
import { AUTHENTICATION_BANNER_FEATURES } from "./authentication-banner-features";

/**
 * How long one panel holds. Reading a miniature takes about three seconds; eight of them
 * is a forty-second lap, which nobody sits through on a login page — and that is fine.
 * The point is not to show all eight, it is that the product is visibly *doing* something
 * the moment the page opens.
 */
const DWELL_MS: number = 5000;

const REDUCED_MOTION_QUERY: string = "(prefers-reduced-motion: reduce)";

interface AuthenticationBannerSliderProps {
  /**
   * Whether the banner is actually on screen. Passed down rather than re-queried: the
   * banner already runs that media query, and below 769px `.auth-page__right` is only
   * `display: none` — the subtree stays mounted, so without this gate the timer would
   * keep ticking against an invisible panel whose CSS animations the browser has frozen.
   */
  isVisible: boolean;
}

/**
 * The eight feature panels, auto-advancing, with the pill strip that selects them.
 *
 * All of the slider's state lives here rather than in `AuthenticationBanner` on purpose:
 * a tick must not re-render the banner, because that is where `KuikaGrainGradient` is
 * mounted and its `frame` is an init-only seed — re-applying it re-seeks the shader clock
 * and shows up as a jump in the background animation.
 */
export const AuthenticationBannerSlider: React.FC<AuthenticationBannerSliderProps> = ({ isVisible }) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  // Bumped whenever a fresh dwell starts. It is what restarts the progress bar in the
  // two cases the index alone cannot express: clicking the pill that is already active,
  // and resuming after a hover.
  const [cycleKey, setCycleKey] = useState<number>(0);

  const prefersReducedMotion: boolean = useMediaQuery(REDUCED_MOTION_QUERY);
  const isAutoAdvancing: boolean = isVisible && !isPaused && !prefersReducedMotion;

  // A timeout keyed on the slide, not an interval: every path that starts a new dwell
  // changes a dependency, React runs the cleanup first, and reset-on-click falls out of
  // the effect lifecycle instead of needing its own imperative clear.
  useEffect((): (() => void) | undefined => {
    if (!isAutoAdvancing) {
      return undefined;
    }

    const timeoutId: number = window.setTimeout((): void => {
      setActiveIndex((current: number): number => (current + 1) % AUTHENTICATION_BANNER_FEATURES.length);
      setCycleKey((current: number): number => current + 1);
    }, DWELL_MS);

    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [activeIndex, cycleKey, isAutoAdvancing]);

  const handleSelect = useCallback((index: number): void => {
    setActiveIndex(index);
    setCycleKey((current: number): number => current + 1);
  }, []);

  const handlePause = useCallback((): void => {
    setIsPaused(true);
  }, []);

  // Resuming restarts a whole dwell rather than continuing the remainder: a bar that
  // resumes from 40% after the pointer left reads as a glitch, not as a countdown.
  const handleResume = useCallback((): void => {
    setIsPaused(false);
    setCycleKey((current: number): number => current + 1);
  }, []);

  const activeFeature: AuthenticationBannerFeature = AUTHENTICATION_BANNER_FEATURES[activeIndex];
  const ActiveIllustration: AuthenticationBannerIllustration = activeFeature.Illustration;

  return (
    <div
      className="kuika-auth-banner__showcase"
      // Single-sourced from DWELL_MS: the bar and the timer must agree, and hard-coding
      // the duration in the stylesheet guarantees they drift the first time one is tuned.
      style={{ "--kuika-auth-banner-dwell": `${DWELL_MS}ms` } as React.CSSProperties}
      onMouseEnter={handlePause}
      onMouseLeave={handleResume}
      onFocus={handlePause}
      onBlur={handleResume}
    >
      {/* Keyed so the caption cross-fades with the panel instead of swapping text under
          a still frame. */}
      <div className="kuika-auth-banner__caption" key={`caption-${activeFeature.id}`}>
        <span className="kuika-auth-banner__caption-dot" />
        <span className="kuika-auth-banner__caption-name">{activeFeature.label}</span>
        <span className="kuika-auth-banner__caption-note">{activeFeature.note}</span>
      </div>

      {/* The miniatures are decoration: the caption above carries their meaning, and the
          pills carry the names. Nothing inside may be focusable — see
          banner-illustrations.tsx. */}
      <div className="kuika-auth-banner__viewport" aria-hidden="true">
        <div className="kuika-auth-banner__panel" key={activeFeature.id}>
          <ActiveIllustration />
        </div>
      </div>

      {/* Plain buttons, not a `tablist`: APG tab semantics without arrow-key roving
          tabindex would promise keyboard behaviour that is not there. */}
      <div className="kuika-auth-banner__strip">
        {AUTHENTICATION_BANNER_FEATURES.map((feature: AuthenticationBannerFeature, index: number) => {
          const isActive: boolean = index === activeIndex;

          return (
            <button
              key={feature.id}
              type="button"
              className={classNames("kuika-auth-banner__pill", {
                "kuika-auth-banner__pill--active": isActive
              })}
              aria-label={feature.label}
              aria-current={isActive ? "true" : undefined}
              data-testid={`authBannerPill-${feature.id}`}
              onClick={(): void => handleSelect(index)}
            >
              <span className="kuika-auth-banner__pill-top">
                <KuikaSvg
                  className="kuika-auth-banner__pill-icon"
                  name={feature.iconName}
                  type={feature.iconType}
                  width={14}
                  height={14}
                />
                <span className="kuika-auth-banner__pill-label">{feature.shortLabel}</span>
              </span>

              <span className="kuika-auth-banner__pill-track">
                {/* Remounting on `cycleKey` is what replays the fill: a CSS animation does
                    not re-run just because its class is still applied. */}
                {isActive && <span key={cycleKey} className="kuika-auth-banner__pill-fill" />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export { DWELL_MS };
